// ENG-5635: decide whether a primary_model change should trigger an immediate
// session respawn.
//
// Background: subscription Claude Code agents only pick up their model via the
// `claude --model <alias>` flag the launcher passes at spawn time (ENG-5631).
// Changing primary_model rewrites provision/settings.json and the manager's
// in-memory knownModels map, but does NOT restart the running session — so the
// new model is invisible until the next natural respawn (daily rotation,
// channel/MCP/auth change, crash, or manual restart). This predicate gates a
// proactive hot-reload respawn so the operator's choice applies within a poll.
//
// Kept as a pure function so the gating logic is unit-testable without the
// manager-worker module graph.

export interface ModelChangeRespawnInput {
  /** The model the manager last saw for this agent (knownModels). Undefined/null on first provision. */
  previousModel: string | null | undefined;
  /** The freshly-resolved model for this poll. */
  primaryModel: string | null | undefined;
  /** The agent's framework id (e.g. 'claude-code', 'openclaw', 'nemoclaw'). */
  framework: string;
  /** Whether a healthy persistent session is currently running for this agent. */
  sessionHealthy: boolean;
}

/**
 * True when the model change warrants restarting the live session now.
 *
 * Returns false (let the next natural spawn carry the new model) when:
 * - the framework applies model changes only at spawn time - true for both
 *   claude-code (`--model` launch flag, ENG-5631) and opencode (model baked into
 *   the generated opencode.json the serve reads at boot, ENG-7928). Any other
 *   runtime applies model changes through its own config/gateway path;
 * - no healthy session is running — the upcoming spawn already reads the new
 *   model, so a restart would be pointless (or fight the spawn);
 * - `previousModel` is unset — first provision; the imminent first spawn
 *   carries the model, nothing to switch;
 * - `primaryModel` is unset — the model couldn't be resolved this poll;
 *   respawning would only drop the agent to the tier default, so leave the
 *   live session on its current (known-good) model;
 * - the model is unchanged — no-op, avoids a restart loop.
 */
export function shouldRespawnForModelChange(input: ModelChangeRespawnInput): boolean {
  const { previousModel, primaryModel, framework, sessionHealthy } = input;
  if (framework !== 'claude-code' && framework !== 'opencode') return false;
  if (!sessionHealthy) return false;
  if (!previousModel) return false;
  if (!primaryModel) return false;
  return previousModel !== primaryModel;
}

export interface OpencodeModelRestartInput {
  /** The model the manager last saw for this agent (knownModels). */
  previousModel: string | null | undefined;
  /** The freshly-resolved model for this poll (raw primary_model form). */
  primaryModel: string | null | undefined;
  /** `toOpencodeModel(primaryModel)` - the value that SHOULD be in opencode.json. */
  provisionedModel: string;
  /** The `model` actually present in the on-disk opencode.json (null if unreadable). */
  onDiskModel: string | null;
  /** Whether a healthy opencode serve is currently running for this agent. */
  sessionHealthy: boolean;
}

export interface OpencodeModelRestartDecision {
  /** Stop the serve so the poll loop respawns it against the fresh opencode.json. */
  restart: boolean;
  /** Stamp primaryModel into knownModels - only once the change is durably on disk. */
  recordModel: boolean;
}

/**
 * ENG-7928: gate the opencode model hot-reload TRANSACTIONALLY. opencode reads
 * its model from opencode.json, written by the provision step earlier in the
 * poll. If provisioning has not yet produced the new model on disk
 * (`onDiskModel !== provisionedModel` - a failed or stale provision), defer BOTH
 * the restart AND recording the model, so the next poll retries after a
 * successful provision. Otherwise a stale provision would (a) respawn the serve
 * on the OLD config and (b) stamp knownModels to the new model, masking the
 * still-pending change forever. Only once the file reflects the change do we
 * respawn (per shouldRespawnForModelChange) and record it.
 */
export function decideOpencodeModelRestart(
  input: OpencodeModelRestartInput,
): OpencodeModelRestartDecision {
  const { previousModel, primaryModel, provisionedModel, onDiskModel, sessionHealthy } = input;
  if (onDiskModel !== provisionedModel) {
    // Provisioning has not applied the change to disk yet - retry next poll.
    return { restart: false, recordModel: false };
  }
  const restart = shouldRespawnForModelChange({
    previousModel,
    primaryModel,
    framework: 'opencode',
    sessionHealthy,
  });
  return { restart, recordModel: true };
}
